import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/layout/header";
import { useAuth } from "@/hooks/use-auth";
import { useEffect } from "react";
import { ArrowRight, UploadCloud, Film, Mic, Share } from "lucide-react";

export default function LandingPage() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  
  // Redirect to dashboard if user is already authenticated
  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header transparent />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-white">
        <div className="mx-auto max-w-7xl">
          <div className="relative z-10 bg-white pb-8 sm:pb-16 md:pb-20 lg:w-full lg:max-w-2xl lg:pb-28 xl:pb-32">
            <main className="mx-auto mt-0 pt-10 max-w-7xl px-4 sm:mt-0 sm:pt-12 sm:px-6 md:mt-0 md:pt-16 lg:mt-0 lg:pt-20 lg:px-8 xl:mt-0 xl:pt-28">
              <div className="sm:text-center lg:text-left">
                <h1 className="font-serif text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                  <span className="block">Turn your screenplay</span>
                  <span className="block text-primary">into visual stories</span>
                </h1>
                <p className="mt-3 text-base text-gray-500 sm:mx-auto sm:mt-5 sm:max-w-xl sm:text-lg md:mt-5 md:text-xl lg:mx-0">
                  Upload your screenplay and PreVisly will automatically generate stunning video storyboards. Visualize your story before you shoot, get a feel for your its pacing, and promote your screenplay to non-readers.
                </p>
                <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                  <Button asChild size="lg" className="px-8 py-3 text-base">
                    <Link href="/auth#register">
                      Get Started
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="mt-3 sm:mt-0 sm:ml-3 px-8 py-3 text-base">
                    <Link href="#showcase">
                      View Examples
                    </Link>
                  </Button>
                </div>
              </div>
            </main>
          </div>
        </div>
        <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2 h-full">
          <img 
            className="h-56 w-full object-cover sm:h-72 md:h-96 lg:h-full lg:w-full" 
            src="https://images.unsplash.com/photo-1485846234645-a62644f84728?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80" 
            alt="Screenplay storyboard process" 
            style={{ objectFit: 'cover', objectPosition: 'center top' }}
          />
        </div>
      </div>

      {/* Features Section */}
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">Features</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl font-serif">
              A better way to visualize stories
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Transform words into visuals with our cutting-edge AI technology.
            </p>
          </div>

          <div className="mt-10">
            <dl className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
              <div className="relative">
                <dt>
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-primary text-white">
                    <UploadCloud className="h-6 w-6" />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-medium text-gray-900">Easy Upload</p>
                </dt>
                <dd className="mt-2 ml-16 text-base text-gray-500">
                  Simply upload your screenplay in PDF format and our system automatically processes it.
                </dd>
              </div>

              <div className="relative">
                <dt>
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-primary text-white">
                    <Film className="h-6 w-6" />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-medium text-gray-900">Stunning Renders</p>
                </dt>
                <dd className="mt-2 ml-16 text-base text-gray-500">
                  Choose from multiple visual styles to create the perfect look for your story.
                </dd>
              </div>

              <div className="relative">
                <dt>
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-primary text-white">
                    <Mic className="h-6 w-6" />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-medium text-gray-900">Voice Selection</p>
                </dt>
                <dd className="mt-2 ml-16 text-base text-gray-500">
                  Customize character voices to match your vision and bring dialogue to life.
                </dd>
              </div>

              <div className="relative">
                <dt>
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-primary text-white">
                    <Share className="h-6 w-6" />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-medium text-gray-900">Easy Sharing</p>
                </dt>
                <dd className="mt-2 ml-16 text-base text-gray-500">
                  Download your renders or share them directly with your team and collaborators.
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Showcase Section */}
      <div id="showcase" className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">Showcase</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl font-serif">
              See what creators have made
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
              Browse through examples of screenplay visualizations created with PreVisly.
            </p>
          </div>

          <div className="mt-10 grid gap-5 max-w-lg mx-auto lg:grid-cols-3 lg:max-w-none">
            {/* Showcase Item 1 */}
            <div className="flex flex-col rounded-lg shadow-lg overflow-hidden">
              <div className="flex-shrink-0">
                <img 
                  className="h-48 w-full object-cover" 
                  src="https://images.unsplash.com/photo-1536240478700-b869070f9279?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80" 
                  alt="Screenplay storyboard" 
                />
              </div>
              <div className="flex-1 bg-white p-6 flex flex-col justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-primary">Cinematic Realism</p>
                  <Link href="#" className="block mt-2">
                    <p className="text-xl font-semibold text-gray-900">The Last Sunset</p>
                    <p className="mt-3 text-base text-gray-500">A dramatic western tale visualized with stunning cinematic quality storyboards.</p>
                  </Link>
                </div>
                <div className="mt-6 flex items-center">
                  <div className="flex-shrink-0">
                    <span className="sr-only">James Smith</span>
                    <img 
                      className="h-10 w-10 rounded-full" 
                      src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" 
                      alt="User avatar" 
                    />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">James Smith</p>
                    <div className="flex space-x-1 text-sm text-gray-500">
                      <span>Dec 12, 2023</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Showcase Item 2 */}
            <div className="flex flex-col rounded-lg shadow-lg overflow-hidden">
              <div className="flex-shrink-0">
                <img 
                  className="h-48 w-full object-cover" 
                  src="https://images.unsplash.com/photo-1581905764498-f1b60bae941a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2364&q=80" 
                  alt="Anime Style" 
                />
              </div>
              <div className="flex-1 bg-white p-6 flex flex-col justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-primary">Anime Style</p>
                  <Link href="#" className="block mt-2">
                    <p className="text-xl font-semibold text-gray-900">Garden of Memories</p>
                    <p className="mt-3 text-base text-gray-500">A heartwarming animated story rendered in the magical style inspired by Studio Ghibli.</p>
                  </Link>
                </div>
                <div className="mt-6 flex items-center">
                  <div className="flex-shrink-0">
                    <span className="sr-only">Emily Chen</span>
                    <img 
                      className="h-10 w-10 rounded-full" 
                      src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" 
                      alt="User avatar" 
                    />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Emily Chen</p>
                    <div className="flex space-x-1 text-sm text-gray-500">
                      <span>Nov 28, 2023</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Showcase Item 3 */}
            <div className="flex flex-col rounded-lg shadow-lg overflow-hidden">
              <div className="flex-shrink-0">
                <img 
                  className="h-48 w-full object-cover" 
                  src="https://images.unsplash.com/photo-1578935904021-89c9d0fddab9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2232&q=80" 
                  alt="Comic book style render" 
                />
              </div>
              <div className="flex-1 bg-white p-6 flex flex-col justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-primary">Comic Book</p>
                  <Link href="#" className="block mt-2">
                    <p className="text-xl font-semibold text-gray-900">Steel Guardian</p>
                    <p className="mt-3 text-base text-gray-500">A superhero screenplay visualized in a dynamic comic book art style with bold colors.</p>
                  </Link>
                </div>
                <div className="mt-6 flex items-center">
                  <div className="flex-shrink-0">
                    <span className="sr-only">Marcus Johnson</span>
                    <img 
                      className="h-10 w-10 rounded-full" 
                      src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" 
                      alt="User avatar" 
                    />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Marcus Johnson</p>
                    <div className="flex space-x-1 text-sm text-gray-500">
                      <span>Jan 4, 2024</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <Button asChild>
              <Link href="/showcase">
                View All Showcase Projects <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="bg-white py-16 lg:py-24">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <div className="text-center">
              <h2 className="text-3xl tracking-tight font-extrabold text-gray-900 sm:text-4xl font-serif">
                What creators are saying
              </h2>
              <p className="mt-3 max-w-2xl mx-auto text-xl text-gray-500 sm:mt-4">
                PreVisly is helping filmmakers and writers around the world bring their stories to life.
              </p>
            </div>
            <div className="mt-12 space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
              <div className="bg-gray-50 rounded-lg p-6 shadow-sm">
                <div className="flex items-start">
                  <div className="text-primary-400">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="h-8 w-8" 
                      fill="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z"/>
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-base text-gray-500">
                      "PreVisly saved our pre-production timeline. We were able to visualize complex action sequences before shooting, which helped us finalize our shot list and shooting schedule."
                    </p>
                    <div className="mt-4">
                      <p className="text-base font-semibold text-gray-900">Sarah Davis</p>
                      <p className="text-sm text-gray-500">Independent Filmmaker</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-6 shadow-sm">
                <div className="flex items-start">
                  <div className="text-primary-400">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="h-8 w-8" 
                      fill="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z"/>
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-base text-gray-500">
                      "When pitching to investors, having a visual render of my screenplay made all the difference. It helped them see my vision clearly and secured our funding."
                    </p>
                    <div className="mt-4">
                      <p className="text-base font-semibold text-gray-900">Michael Rodriguez</p>
                      <p className="text-sm text-gray-500">Screenwriter & Producer</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-primary-700">
        <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl font-serif">
            <span className="block">Ready to visualize your screenplay?</span>
          </h2>
          <p className="mt-4 text-lg leading-6 text-primary-100">
            Join creators worldwide who are transforming their written stories into compelling visuals.
          </p>
          <Button asChild size="lg" className="mt-8 bg-white text-primary-700 hover:bg-primary-50">
            <Link href="/auth">
              Get started for free
            </Link>
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white">
        <div className="max-w-7xl mx-auto py-12 px-4 overflow-hidden sm:px-6 lg:px-8">
          <nav className="-mx-5 -my-2 flex flex-wrap justify-center" aria-label="Footer">
            <div className="px-5 py-2">
              <Link href="/showcase">
                <div className="text-base text-gray-500 hover:text-gray-900">
                  Showcase
                </div>
              </Link>
            </div>

            <div className="px-5 py-2">
              <Link href="/faq">
                <div className="text-base text-gray-500 hover:text-gray-900">
                  FAQ
                </div>
              </Link>
            </div>

            <div className="px-5 py-2">
              <Link href="/terms">
                <div className="text-base text-gray-500 hover:text-gray-900">
                  Terms
                </div>
              </Link>
            </div>

            <div className="px-5 py-2">
              <Link href="/privacy">
                <div className="text-base text-gray-500 hover:text-gray-900">
                  Privacy
                </div>
              </Link>
            </div>
          </nav>
          <p className="mt-8 text-center text-base text-gray-400">
            &copy; 2024 PreVisly, Inc. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
